/**
 * Shared builders for unreachable scenery (lifted from the shared prototype
 * engine's `engine/src/backdrop-kit.js`): row houses with one window
 * vocabulary each, walls split into bay × storey cells at the town's pixel
 * density, gables, gable roofs, chimneys. Walls, gables and yard surfaces go
 * into one `AtlasKit` (one draw on the backdrop material); roofs, chimneys and
 * timber reuse the town's materials, so they batch into draws the town
 * already pays for. Each builder has its own seeded stream (mulberry32).
 */
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import { AtlasKit, CELLS as C, type V3 } from './backdrop-art.ts';
import type { Ctx } from './kit.ts';

export const BAY = 1.25;
export const STOREY = 1.6;

/** Dark, saturated, warm wall tints (linear multipliers on a mid-value stone render). */
export const TINTS = {
  straw: [1.0, 0.9, 0.7],
  ochre: [1.0, 0.8, 0.5],
  amber: [1.0, 0.72, 0.42],
  rust: [0.98, 0.62, 0.5],
  clay: [0.9, 0.58, 0.44],
  sage: [0.72, 0.8, 0.56],
  teal: [0.56, 0.72, 0.72],
  umber: [0.8, 0.64, 0.5],
  warm: [0.95, 0.85, 0.72],
} as const satisfies Readonly<Record<string, V3>>;
const TINT_ORDER: readonly V3[] = Object.values(TINTS);
const GROUND_STONE: V3 = [0.82, 0.77, 0.7];

export type Style = 'plaster' | 'timber' | 'brick';
export type Face = 'south' | 'east' | 'west' | 'north';
export type RoofKind = 'gableX' | 'gableZ' | 'hip' | 'flat';

const WINDOWS: Readonly<Record<Style, { readonly primary: readonly number[]; readonly accent: readonly number[]; readonly night: readonly number[] }>> = {
  plaster: {
    primary: [C.winGreen, C.winRed, C.winPlain, C.winFlowers, C.winCurtain, C.winHalf, C.winPair, C.winOffset],
    accent: [C.winClosed, C.winIvy, C.winFlowers, C.winHalf, C.winCurtain],
    night: [C.winNight, C.winNight2],
  },
  timber: {
    primary: [C.timberWin, C.timberShut, C.timberCurtain, C.timberSmall],
    accent: [C.timberX, C.timberBrace, C.timberIvy, C.timberShut],
    night: [C.timberNight, C.timberNight2],
  },
  brick: {
    primary: [C.brickWin, C.brickShut, C.brickCurtain],
    accent: [C.brickShut, C.brick, C.brickCurtain],
    night: [C.brickLit],
  },
};
const LIT: Readonly<Record<Style, number>> = { plaster: C.winLit, timber: C.timberLit, brick: C.brickLit };
const BLANK: Readonly<Record<Style, number>> = { plaster: C.plaster, timber: C.timberStud, brick: C.brick };
const GROUND = [C.archWin, C.barred, C.stone, C.archWin, C.stoneIvy];
const GABLES: Readonly<Record<Style, readonly number[]>> = {
  plaster: [C.gableRound, C.gableLouvre, C.plaster],
  timber: [C.timberStud, C.timberSmall],
  brick: [C.gableRound, C.gableLouvre],
};
const RHYTHMS = ['regular', 'regular', 'paired', 'sparse', 'offset'] as const;

/** Distance of a point beyond the walkable rails (x −20.15…21.15, z ±16.15). */
const beyondRails = (x: number, z: number): number => Math.hypot(Math.max(0, -20.15 - x, x - 21.15), Math.max(0, -16.15 - z, z - 16.15));

export type HouseOptions = {
  readonly x: number;
  readonly z: number;
  readonly bays?: number;
  readonly deep?: number;
  readonly floors?: number;
  readonly base?: number;
  readonly style?: Style;
  readonly tint?: V3;
  readonly roof?: RoofKind;
  readonly roofMat?: THREE.Material;
  readonly face?: Face;
  readonly side?: Face;
  readonly stoneGround?: boolean;
  readonly lit?: number;
  readonly door?: boolean;
  readonly passage?: boolean;
  readonly dormers?: boolean;
  readonly chimneys?: number;
  readonly rise?: number | null;
  readonly cheap?: boolean;
};

export type BackdropBuilder = {
  readonly kit: AtlasKit;
  readonly random: () => number;
  readonly pick: <T>(list: readonly T[]) => T;
  readonly gableRoof: (p: THREE.Object3D, x: number, y: number, z: number, width: number, depth: number, rise: number, axis: 'x' | 'z', m: THREE.Material, overhang?: number) => void;
  readonly chimney: (p: THREE.Object3D, x: number, y: number, z: number, h?: number) => void;
  /** A backdrop house; `face` is the street side the camera sees in detail. Returns its roof top. */
  readonly house: (p: THREE.Object3D, o: HouseOptions) => number;
};

export const createBackdropBuilder = (ctx: Ctx, seed: number): BackdropBuilder => {
  const { m, box, cylinder } = ctx;
  const kit = new AtlasKit();
  const random = seededRandom(seed);
  const pick = <T>(list: readonly T[]): T => {
    const v = list[Math.floor(random() * list.length)] ?? list[0];
    if (v === undefined) throw new Error('backdrop: pick from an empty list');
    return v;
  };
  let lastTint: V3 | null = null;

  const wall = (frame: (u: number, v: number) => V3, width: number, height: number, cellFor: (col: number, row: number, cols: number, rows: number) => number, tint: V3, occlusion = 0.62): void => {
    const cols = Math.max(1, Math.round(width / BAY));
    const cw = width / cols;
    const rows = Math.max(1, Math.round(height / STOREY));
    const rh = height / rows;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const u0 = col * cw;
        const u1 = u0 + cw;
        const v0 = row * rh;
        const v1 = v0 + rh;
        const shade: readonly [number, number] = [row === 0 ? occlusion : 0.9 + 0.1 * (row / rows), row === 0 ? Math.min(1, occlusion + 0.38) : 0.94 + 0.06 * ((row + 1) / rows)];
        kit.quad(frame(u0, v0), frame(u1, v0), frame(u1, v1), frame(u0, v1), cellFor(col, row, cols, rows), tint, undefined, shade);
      }
    }
  };

  const gable = (frame: (u: number, v: number) => V3, width: number, rise: number, cellChooser: (col: number) => number, tint: V3): void => {
    const half = width / 2;
    const chosen = new Map<number, number>();
    const cellFor = (col: number): number => {
      const known = chosen.get(col);
      if (known !== undefined) return known;
      const c = cellChooser(col);
      chosen.set(col, c);
      return c;
    };
    const seams = [0];
    for (let k = BAY / 2; k < half; k += BAY) seams.push(k);
    seams.push(half);
    const xs = [...seams.map((v) => -v).reverse().slice(0, -1), ...seams];
    const roofY = (x: number): number => rise * (1 - Math.abs(x) / half);
    for (let i = 0; i < xs.length - 1; i += 1) {
      const xa = xs[i] ?? 0;
      const xb = xs[i + 1] ?? 0;
      const colStart = Math.floor((xa + BAY / 2 + 1e-6) / BAY) * BAY - BAY / 2;
      const cell = cellFor(Math.round((colStart + BAY / 2) / BAY));
      const ua = (xa - colStart) / BAY;
      const ub = (xb - colStart) / BAY;
      const ya = roofY(xa);
      const yb = roofY(xb);
      for (let y0 = 0; y0 < Math.max(ya, yb) - 1e-4; y0 += STOREY) {
        const ta = Math.min(ya, y0 + STOREY);
        const tb = Math.min(yb, y0 + STOREY);
        if (ta <= y0 && tb <= y0) continue;
        const [U0, V0, U1, V1] = AtlasKit.uvRect(cell, [ua, 0, ub, 1]);
        const va = Math.max(0, (ta - y0) / STOREY);
        const vb = Math.max(0, (tb - y0) / STOREY);
        const P: readonly V3[] = [frame(xa + half, y0), frame(xb + half, y0), frame(xb + half, Math.max(y0, tb)), frame(xa + half, Math.max(y0, ta))];
        const p0 = new THREE.Vector3(...(P[0] ?? [0, 0, 0]));
        const n = new THREE.Vector3()
          .subVectors(new THREE.Vector3(...(P[1] ?? [0, 0, 0])), p0)
          .cross(new THREE.Vector3().subVectors(new THREE.Vector3(...(P[3] ?? [0, 0, 0])), p0))
          .normalize();
        const uv: readonly (readonly [number, number])[] = [
          [U0, V0],
          [U1, V0],
          [U1, V0 + (V1 - V0) * vb],
          [U0, V0 + (V1 - V0) * va],
        ];
        for (const idx of [0, 1, 2, 0, 2, 3]) {
          kit.pos.push(...(P[idx] ?? [0, 0, 0]));
          kit.nor.push(n.x, n.y, n.z);
          kit.uv.push(...(uv[idx] ?? [0, 0]));
          kit.col.push(...tint.map((c) => c * 0.96));
        }
      }
    }
  };

  const slab = (p: THREE.Object3D, cx: number, cy: number, cz: number, len: number, thick: number, run: number, angle: number, axis: 'x' | 'z', material: THREE.Material): void => {
    const o = axis === 'x' ? box(p, cx, cy, cz, len, thick, run, material) : box(p, cx, cy, cz, run, thick, len, material);
    if (axis === 'x') o.rotation.x = angle;
    else o.rotation.z = angle;
  };

  const gableRoof: BackdropBuilder['gableRoof'] = (p, x, y, z, width, depth, rise, axis, material, overhang = 0.28) => {
    const span = axis === 'x' ? depth : width;
    const len = (axis === 'x' ? width : depth) + overhang * 2;
    const ang = Math.atan2(rise, span / 2);
    const c = Math.cos(ang);
    const sn = Math.sin(ang);
    const run = Math.hypot(span / 2, rise) + overhang;
    const h = (run / 2) * c + 0.07 * sn;
    const cy = y + rise - (run / 2) * sn + 0.07 * c;
    for (const sgn of [-1, 1]) {
      if (axis === 'x') slab(p, x, cy, z + sgn * h, len, 0.14, run, sgn * ang, 'x', material);
      else slab(p, x + sgn * h, cy, z, len, 0.14, run, -sgn * ang, 'z', material);
    }
    if (axis === 'x') box(p, x, y + rise + 0.1, z, len, 0.14, 0.2, material);
    else box(p, x, y + rise + 0.1, z, 0.2, 0.14, len, material);
  };

  const chimney: BackdropBuilder['chimney'] = (p, x, y, z, h = 1.4) => {
    box(p, x, y + h / 2, z, 0.5, h, 0.5, m.stone);
    box(p, x, y + h + 0.06, z, 0.62, 0.12, 0.62, m.cream);
    if (random() < 0.7) cylinder(p, x + (random() - 0.5) * 0.18, y + h + 0.26, z, 0.09, 0.3, m.red);
  };

  const house: BackdropBuilder['house'] = (p, o) => {
    const { x, z, bays = 3, deep = 3, floors = 3, base = 0, style = 'plaster', roof = 'gableX', roofMat = m.roof, face = 'south', side = 'east', stoneGround = true, lit = 0.12, door = true, passage = false, dormers = true, chimneys = 1, rise = null, cheap = false } = o;
    let tint: V3 = o.tint ?? TINTS.straw;
    // Never repeat the neighbour's tint.
    if (tint === lastTint) tint = TINT_ORDER[(TINT_ORDER.indexOf(tint) + 1 + Math.floor(random() * 3)) % TINT_ORDER.length] ?? tint;
    lastTint = tint;
    const far = Math.min(1, Math.max(0, (beyondRails(x, z) - 3) / 15));
    const k = 0.62 * (1 - 0.18 * far) * (cheap ? 0.86 : 1);
    const warmth: V3 = [1, 1 - 0.05 * far, 1 - 0.14 * far];
    const tinted: V3 = [tint[0] * k * warmth[0], tint[1] * k * warmth[1], tint[2] * k * warmth[2]];
    const W = bays * BAY;
    const D = deep * BAY;
    const H = floors * STOREY;
    const x0 = x - W / 2;
    const x1 = x + W / 2;
    const z0 = z - D / 2;
    const z1 = z + D / 2;
    const groundTint: V3 = stoneGround ? [GROUND_STONE[0] * k * 1.1 * warmth[0], GROUND_STONE[1] * k * 1.1 * warmth[1], GROUND_STONE[2] * k * 1.1 * warmth[2]] : tinted;
    const topTint: V3 = [tinted[0] * 1.07, tinted[1], tinted[2] * 0.9];
    const doorBay = Math.floor(random() * 99);
    const vocab = WINDOWS[style];
    const primary = pick(vocab.primary);
    const rhythm = pick(RHYTHMS);
    const blankBay = (col: number, cols: number): boolean => (rhythm === 'paired' ? cols > 2 && col % 3 === 2 : rhythm === 'offset' ? col === cols - 1 && cols > 2 : false);
    const detail =
      (cols: number, cheapFace: boolean) =>
      (col: number, row: number): number => {
        if (row === 0 && stoneGround) {
          if (door && col === doorBay % cols) return pick([C.doorTeal, C.doorBrown]);
          if (passage && col === (doorBay + 1) % cols && cols > 1) return C.passage;
          if (random() < 0.08) return C.shopLit;
          return cheapFace ? C.stone : pick(GROUND);
        }
        if (row === 0 && door && col === doorBay % cols) return pick([C.doorTeal, C.doorBrown]);
        if (blankBay(col, cols)) return BLANK[style];
        if (cheapFace && random() < (cheap ? 0.5 : 0.28)) return BLANK[style];
        if (rhythm === 'sparse' && random() < 0.35) return BLANK[style];
        const roll = random();
        if (roll < lit) return LIT[style];
        if (roll < lit + 0.22) return pick(vocab.night);
        if (roll < lit + 0.4) return pick(vocab.accent);
        return primary;
      };
    const tintFor = (row: number): V3 => (row === 0 && stoneGround ? groundTint : row === floors - 1 ? topTint : tinted);
    const storeyWall = (frame: (u: number, v: number) => V3, width: number, cellFor: (col: number, row: number) => number): void => {
      for (let row = 0; row < floors; row += 1) wall((u, v) => frame(u, v + row * STOREY), width, STOREY, (col) => cellFor(col, row), tintFor(row), row === 0 ? 0.42 : 0.92);
    };
    const frames: Readonly<Record<Face, (u: number, v: number) => V3>> = {
      south: (u, v) => [x0 + u, base + v, z1],
      east: (u, v) => [x1, base + v, z1 - u],
      west: (u, v) => [x0, base + v, z0 + u],
      north: (u, v) => [x1 - u, base + v, z0],
    };
    const widths: Readonly<Record<Face, number>> = { south: W, north: W, east: D, west: D };
    for (const f of ['south', 'east', 'west', 'north'] as const) {
      const main = f === face;
      const second = f === side;
      if (main || second) {
        const cols = Math.round(widths[f] / BAY);
        storeyWall(frames[f], widths[f], detail(cols, !main || cheap));
      } else {
        // Unseen faces: one stretched plain panel per wall.
        const frame = frames[f];
        kit.quad(frame(0, 0), frame(widths[f], 0), frame(widths[f], H), frame(0, H), BLANK[style], [tinted[0] * 0.75, tinted[1] * 0.75, tinted[2] * 0.75], [0, 0, 0.5, 0.5]);
      }
    }
    const top = base + H;
    box(p, x, top - 0.05, z, W + 0.12, 0.12, D + 0.12, m.darkWood);
    const r = rise ?? (roof === 'gableZ' ? W * (0.45 + random() * 0.25) : D * (0.38 + random() * 0.2));
    if (roof === 'gableX') {
      gableRoof(p, x, top, z, W, D, r, 'x', roofMat);
      for (const sx of [-1, 1]) {
        const gx = sx < 0 ? x0 : x1;
        gable(sx > 0 ? (u, v) => [gx, top + v, z1 - u] : (u, v) => [gx, top + v, z0 + u], D, r, () => pick(GABLES[style]), tinted);
      }
      if (dormers && bays >= 3 && random() < 0.75) {
        const n = bays >= 4 && random() < 0.5 ? 2 : 1;
        for (let i = 0; i < n; i += 1) {
          const dx = n === 1 ? (random() - 0.5) * (W - 2) : (i ? 1 : -1) * W * 0.24;
          const sgn = face === 'north' ? -1 : 1;
          const dz = z + sgn * (D / 2 - D * 0.28);
          const dy = top + r * 0.3;
          kit.box(x + dx, dy + 0.42, dz, 0.95, 0.84, 0.9, { top: null, front: random() < 0.3 ? C.winLit : C.winPlain, east: C.plaster, west: C.plaster, back: null }, tinted);
          const g = new THREE.Group();
          g.position.set(x + dx, dy + 0.84, dz);
          p.add(g);
          gableRoof(g, 0, 0, 0, 0.95, 0.9, 0.42, 'z', roofMat, 0.12);
        }
      }
    } else if (roof === 'gableZ') {
      gableRoof(p, x, top, z, W, D, r, 'z', roofMat);
      for (const sz of [-1, 1]) {
        const gz = sz > 0 ? z1 : z0;
        gable(sz > 0 ? (u, v) => [x0 + u, top + v, gz] : (u, v) => [x1 - u, top + v, gz], W, r, (col) => (col === 0 ? pick(GABLES[style]) : style === 'timber' ? C.timberStud : C.plaster), tinted);
      }
    } else if (roof === 'hip') {
      ctx.hipRoof(p, x, top, z, W + 0.5, D + 0.5, r, roofMat);
    } else {
      // Parapet roof with a lead flat; reads as a taller townhouse.
      kit.box(x, top + 0.25, z, W + 0.1, 0.5, D + 0.1, { top: C.darkWood, front: BLANK[style], east: BLANK[style], west: BLANK[style], back: BLANK[style] }, tinted);
      box(p, x, top + 0.53, z, W + 0.24, 0.08, D + 0.24, m.cream);
    }
    for (let i = 0; i < chimneys; i += 1) {
      const cx = x + (i ? -1 : 1) * W * (0.2 + random() * 0.18);
      const cz = z + (random() - 0.5) * D * 0.4;
      chimney(p, cx, top + (roof === 'flat' ? 0.4 : r * 0.55), cz, 1 + random() * 1.1 + (roof === 'flat' ? 0 : r * 0.35));
    }
    return top + r;
  };

  return { kit, random, pick, gableRoof, chimney, house };
};
